import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@artstorefronts/ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MockImage } from './MockImage';
import DownloadButton from './DownloadButton';
import MockSaveButton from './MockSaveButton';
import { getMockImageUrl } from '../utils';
import { useSavedContent } from '../context/savedContent';
import { ImageDimensions, MockDimensions } from '../types';

interface PaletteBarProps {
  palette: string[];
  className?: string;
}

const PaletteBar = React.memo(({ palette, className }: PaletteBarProps) => {
  if (!palette?.length) return null;

  return (
    <div className={className}>
      {palette.map((color: string, index: number) => (
        <div key={index} className="flex-1" style={{ backgroundColor: color }} />
      ))}
    </div>
  );
});

interface Mock {
  id: string;
  uuid: string;
  description: string;
  tags: [string];
  palette: [string];
}

interface MockProps {
  asfId: string;
  mock: Mock;
  imageId: number;
  onClose: () => void;
  imageDimensions: ImageDimensions;
  mockDimensions: MockDimensions;
  width?: number;
  height?: number;
}

function Mock({ asfId, mock, imageId, onClose, imageDimensions, mockDimensions }: MockProps) {
  const { t: tc } = useTranslation('common');
  const { mockSaves } = useSavedContent();

  const isSaved = useMemo(() => {
    return mockSaves.some((save) => save.imageId === imageId.toString() && save.mockId === mock.id);
  }, [mockSaves, imageId, mock.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed top-0 left-0 right-0 bottom-0 z-50 bg-white shadow-[0_0_0_10px_rgba(255,255,255,1)] rounded-xs"
      onClick={onClose}
    >
      <div className="h-full w-full flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative w-full h-full"
          onClick={(e) => e.stopPropagation()}
        >
          <MockImage
            mock={mock}
            asfId={asfId}
            imageId={imageId}
            imageDimensions={imageDimensions}
            mockDimensions={mockDimensions}
            className="w-full h-full"
          />
          <PaletteBar
            palette={mock.palette}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 h-8 flex rounded-lg overflow-hidden w-64"
          />
          <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
            <Button
              variant="ghost"
              className="bg-black/70 hover:bg-black/90 text-white hover:text-white"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
            <DownloadButton
              imageUrl={getMockImageUrl(asfId, mock.uuid, imageId, imageDimensions, mockDimensions)}
              title={tc('download')}
              className="bg-black/70 hover:bg-black/90 text-white hover:text-white"
              mockupId={mock.uuid}
              imageId={String(imageId)}
            />
            <MockSaveButton
              mockId={mock.id}
              imageId={imageId}
              variant="ghost"
              isSaved={isSaved}
              className="bg-black/70 hover:bg-black/90 text-white hover:text-white"
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Mock;
